import { IonModal, IonButton, IonIcon } from "@ionic/react";
import { closeOutline, lockClosed } from "ionicons/icons";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { redirectToPremium } from "../../../utils/premiumRedirect";
import "./PermissionPopupModal.min.css";

const normalizePopupActions = (actions) => {
  if (!Array.isArray(actions)) return [];
  return actions.filter((action) => action && action.button_text);
};

const resolveButtonNavigation = async ({
  action,
  history,
  pathname,
}) => {
  const text = String(action.button_text || "").toLowerCase();

  const configured =
    typeof action.button_link === "string" ? action.button_link.trim() : "";

  if (action.button_has_accent_background) {
    if (text.includes("gratis") || text.includes("prova")) {
      history.push(configured || "/percorso-gratuito");
      return;
    }
  }

  if (text.includes("premium") || !action.button_has_accent_background) {
    // `button_link` is the destination set in the CRM's permissions manager.
    // It was already being sent by the API and simply never read here — the
    // destination was inferred from the button's label instead, so editing the
    // field in the CRM changed nothing.
    await redirectToPremium({ history, pathname, url: action.button_link });
    return;
  }

  history.push("/prodotti");
};

// `title` (CRM «Titolo popup», client 20/10/2026) is optional: without it the
// popup looks as before. `extraLink` ({ label, to }) adds one more way out,
// e.g. «Gestisci le mie chat» on the Premium Lite chat limit.
const PermissionPopupModal = ({ isOpen, setIsOpen, message = "", title = null, extraLink = null }) => {
  const history = useHistory();
  const location = useLocation();
  const popupActions = useSelector(
    (state) => state.permissions?.popupActions ?? [],
  );

  const close = () => setIsOpen(false);
  const actions = normalizePopupActions(popupActions);

  const handleButtonClick = async (action) => {
    close();
    try {
      await resolveButtonNavigation({
        action,
        history,
        pathname: location.pathname,
      });
    } catch {
      history.push("/shopping-widget");
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={close}
      canDismiss={true}
      backdropDismiss={true}
      showBackdrop={true}
      className="permission-popup-modal"
    >
      <div className="permission-popup-card">
        <button
          type="button"
          className="permission-popup-close"
          onClick={close}
          aria-label="Chiudi"
        >
          <IonIcon icon={closeOutline} />
        </button>

        <div className="permission-popup-eyebrow">
          <IonIcon icon={lockClosed} />
          <span>Funzione riservata</span>
        </div>

        {title ? <div className="permission-popup-title">{title}</div> : null}

        {message ? (
          <div className="permission-popup-message">{message}</div>
        ) : null}

        {actions.length > 0 ? (
          <div className="permission-popup-actions">
            {actions.map((action, index) => {
              const isAccent = !!action.button_has_accent_background;
              return (
                <div key={`${action.button_text}-${index}`} className="permission-popup-action">
                  <IonButton
                    expand="block"
                    fill={isAccent ? "solid" : "outline"}
                    className={`permission-popup-btn ${
                      isAccent
                        ? "permission-popup-btn--accent"
                        : "permission-popup-btn--outline"
                    }`}
                    onClick={() => handleButtonClick(action)}
                  >
                    {action.button_text}
                  </IonButton>
                  {action.button_description ? (
                    <p className="permission-popup-btn-desc">
                      {action.button_description}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}

        {extraLink ? (
          <IonButton
            expand="block"
            fill="outline"
            className="permission-popup-btn permission-popup-btn--outline"
            onClick={() => {
              close();
              history.push(extraLink.to);
            }}
          >
            {extraLink.label}
          </IonButton>
        ) : null}

        <button type="button" className="permission-popup-dismiss" onClick={close}>
          Chiudi
        </button>
      </div>
    </IonModal>
  );
};

export default PermissionPopupModal;
